import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { differenceInDays, format, parseISO, isValid } from 'date-fns';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899'];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? format(d, 'MM/dd/yyyy') : '—';
  } catch { return '—'; }
};

const statusBadge = (status) => {
  const map = {
    'In Progress': 'bg-blue-100 text-blue-700',
    'Planning': 'bg-yellow-100 text-yellow-700',
    'Completed': 'bg-green-100 text-green-700',
    'On Hold': 'bg-gray-100 text-gray-600',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map?.[status] || 'bg-gray-100 text-gray-600'}`}>{status || '—'}</span>;
};

// Simple Gantt chart for a list of projects
const PMGanttChart = ({ projects, color }) => {
  const validProjects = useMemo(() =>
    (projects || [])?.filter(p => p?.start_date && p?.end_date),
    [projects]
  );

  if (validProjects?.length === 0) {
    return <p className="text-sm text-gray-400 italic">No projects with date information</p>;
  }

  const allDates = validProjects?.flatMap(p => [parseISO(p?.start_date), parseISO(p?.end_date)]);
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  const totalDays = Math.max(differenceInDays(maxDate, minDate) + 1, 1);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Header: month markers */}
        <div className="flex mb-2 pl-40">
          <div className="flex-1 flex">
            {(() => {
              const markers = [];
              let cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
              while (cur <= maxDate) {
                const offset = Math.max(differenceInDays(cur, minDate), 0);
                const pct = (offset / totalDays) * 100;
                markers?.push(
                  <div key={cur?.toISOString()} className="absolute text-xs text-gray-400" style={{ left: `${pct}%` }}>
                    {format(cur, 'MMM yy')}
                  </div>
                );
                cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
              }
              return <div className="relative w-full h-5">{markers}</div>;
            })()}
          </div>
        </div>

        {/* Rows */}
        {validProjects?.map((project, idx) => {
          const start = parseISO(project?.start_date);
          const end = parseISO(project?.end_date);
          const startOffset = Math.max(differenceInDays(start, minDate), 0);
          const duration = Math.max(differenceInDays(end, start) + 1, 1);
          const leftPct = (startOffset / totalDays) * 100;
          const widthPct = (duration / totalDays) * 100;

          return (
            <div key={project?.id} className="flex items-center mb-1.5 gap-2">
              <div className="w-40 flex-shrink-0 text-xs text-gray-700 font-medium truncate pr-2" title={project?.project_name}>
                {project?.project_name?.length > 20 ? project?.project_name?.slice(0, 20) + '…' : project?.project_name}
              </div>
              <div className="flex-1 relative h-6 bg-gray-100 rounded">
                <div
                  className="absolute h-full rounded flex items-center px-1.5 text-white text-xs font-medium overflow-hidden"
                  style={{
                    left: `${leftPct}%`,
                    width: `${Math.max(widthPct, 1)}%`,
                    backgroundColor: color || COLORS?.[idx % COLORS?.length]
                  }}
                  title={`${formatDate(project?.start_date)} – ${formatDate(project?.end_date)}`}
                >
                  <span className="truncate">{project?.project_number}</span>
                </div>
              </div>
              <div className="w-20 flex-shrink-0 text-xs text-gray-500 text-right">
                {duration}d
              </div>
            </div>
          );
        })}

        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <span>Start: {format(minDate, 'MMM d, yyyy')}</span>
          <span>End: {format(maxDate, 'MMM d, yyyy')}</span>
          <span>Span: {totalDays} days</span>
        </div>
      </div>
    </div>
  );
};

const PMCard = ({ pm, projects, color, index }) => {
  const [expanded, setExpanded] = useState(false);
  const totalValue = projects?.reduce((sum, p) => sum + parseFloat(p?.total_budget || 0), 0);
  const statusCounts = projects?.reduce((acc, p) => {
    acc[p.status] = (acc?.[p?.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* PM Header */}
      <div className="px-6 py-4 border-b border-gray-100" style={{ borderLeftWidth: 4, borderLeftColor: color }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: color }}>
              {(pm?.nickname || pm?.full_name)?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{pm?.nickname || pm?.full_name || pm?.email || 'Unknown PM'}</h3>
              <p className="text-xs text-gray-500">{pm?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{projects?.length}</p>
              <p className="text-xs text-gray-500">Projects</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalValue)}</p>
              <p className="text-xs text-gray-500">Total Value</p>
            </div>
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {expanded ? 'Collapse' : 'Expand'}
              <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>
          </div>
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(statusCounts)?.map(([status, count]) => (
            <span key={status} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {status}: {count}
            </span>
          ))}
        </div>
      </div>
      {/* Expanded content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Gantt Chart */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Icon name="BarChart2" size={15} className="text-gray-400" />
              Project Timeline (Gantt)
            </h4>
            <PMGanttChart projects={projects} color={color} />
          </div>

          {/* Project Detail Table */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Icon name="List" size={15} className="text-gray-400" />
              Project Details
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Project #</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Project Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Customer</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Status</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Budget</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Start</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">End</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {projects?.map(p => (
                    <tr key={p?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-2 px-3 text-gray-500 font-mono text-xs">{p?.project_number}</td>
                      <td className="py-2 px-3 font-medium text-gray-800">{p?.project_name}</td>
                      <td className="py-2 px-3 text-gray-600">{p?.customer_name || '—'}</td>
                      <td className="py-2 px-3">{statusBadge(p?.status)}</td>
                      <td className="py-2 px-3 font-semibold text-gray-800">{formatCurrency(p?.total_budget)}</td>
                      <td className="py-2 px-3 text-gray-500">{formatDate(p?.start_date)}</td>
                      <td className="py-2 px-3 text-gray-500">{formatDate(p?.end_date)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5 min-w-12">
                            <div className="h-1.5 rounded-full" style={{ width: `${p?.completion_percentage || 0}%`, backgroundColor: color }} />
                          </div>
                          <span className="text-xs text-gray-500 w-8">{p?.completion_percentage || 0}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PMReportingTab = () => {
  const [loading, setLoading] = useState(true);
  const [pmUsers, setPmUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedPm, setSelectedPm] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pmsResult, projsResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, full_name, first_name, last_name, nickname, email, avatar_url')?.eq('is_pm', true)?.eq('is_active', true)?.order('full_name'),
        supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false })
      ]);
      setPmUsers(pmsResult?.data || []);
      setProjects(projsResult?.data || []);
    } catch (err) {
      console.error('PM Reporting fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group projects by PM
  const pmData = useMemo(() => {
    return pmUsers?.map((pm, idx) => ({
      pm,
      projects: projects?.filter(p => p?.assigned_pm_id === pm?.id),
      color: COLORS?.[idx % COLORS?.length]
    }))?.filter(entry => selectedPm === 'all' || entry?.pm?.id === selectedPm);
  }, [pmUsers, projects, selectedPm]);

  // Unassigned projects
  const unassignedProjects = useMemo(() =>
    projects?.filter(p => !p?.assigned_pm_id),
    [projects]
  );

  // Summary stats
  const totalAssigned = useMemo(() => projects?.filter(p => p?.assigned_pm_id)?.length, [projects]);
  const totalValue = useMemo(() => projects?.reduce((s, p) => s + parseFloat(p?.total_budget || 0), 0), [projects]);
  const assignedValue = useMemo(() => projects?.filter(p => p?.assigned_pm_id)?.reduce((s, p) => s + parseFloat(p?.total_budget || 0), 0), [projects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-500 text-sm">Loading PM report data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm">
          <div className="p-3 rounded-lg bg-blue-50 text-blue-600"><Icon name="Users" size={22} /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Project Managers</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{pmUsers?.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm">
          <div className="p-3 rounded-lg bg-green-50 text-green-600"><Icon name="Briefcase" size={22} /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Assigned Projects</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{totalAssigned}</p>
            <p className="text-xs text-gray-400 mt-0.5">{unassignedProjects?.length} unassigned</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm">
          <div className="p-3 rounded-lg bg-purple-50 text-purple-600"><Icon name="DollarSign" size={22} /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Portfolio Value</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{formatCurrency(totalValue)}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm">
          <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600"><Icon name="TrendingUp" size={22} /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Assigned Value</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{formatCurrency(assignedValue)}</p>
          </div>
        </div>
      </div>
      {/* Filter */}
      <div className="flex items-center gap-3">
        <Icon name="Filter" size={15} className="text-gray-400" />
        <span className="text-sm text-gray-500 font-medium">Filter by PM:</span>
        <select
          value={selectedPm}
          onChange={e => setSelectedPm(e?.target?.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Project Managers</option>
          {pmUsers?.map(pm => (
            <option key={pm?.id} value={pm?.id}>{pm?.nickname || pm?.full_name || pm?.email}</option>
          ))}
        </select>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors"
        >
          <Icon name="RefreshCw" size={14} />
          Refresh
        </button>
      </div>
      {/* No PMs message */}
      {pmUsers?.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Icon name="UserX" size={40} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-base font-semibold text-gray-700 mb-1">No Project Managers Found</h3>
          <p className="text-sm text-gray-500">Enable the Project Manager toggle for users in Team Administration to see PM reports.</p>
        </div>
      )}
      {/* PM Cards */}
      {pmData?.map(({ pm, projects: pmProjects, color }, index) => (
        <PMCard key={pm?.id} pm={pm} projects={pmProjects} color={color} index={index} />
      ))}
      {/* Unassigned Projects */}
      {selectedPm === 'all' && unassignedProjects?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-amber-500" />
              <h3 className="font-semibold text-gray-800">Unassigned Projects ({unassignedProjects?.length})</h3>
            </div>
            <span className="text-sm text-gray-500">{formatCurrency(unassignedProjects?.reduce((s, p) => s + parseFloat(p?.total_budget || 0), 0))} total value</span>
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Project #</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Project Name</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Customer</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Status</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Budget</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Start</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">End</th>
                </tr>
              </thead>
              <tbody>
                {unassignedProjects?.map(p => (
                  <tr key={p?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-2 px-3 text-gray-500 font-mono text-xs">{p?.project_number}</td>
                    <td className="py-2 px-3 font-medium text-gray-800">{p?.project_name}</td>
                    <td className="py-2 px-3 text-gray-600">{p?.customer_name || '—'}</td>
                    <td className="py-2 px-3">{statusBadge(p?.status)}</td>
                    <td className="py-2 px-3 font-semibold text-gray-800">{formatCurrency(p?.total_budget)}</td>
                    <td className="py-2 px-3 text-gray-500">{formatDate(p?.start_date)}</td>
                    <td className="py-2 px-3 text-gray-500">{formatDate(p?.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PMReportingTab;
